import { Injectable } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { AppService, SKIP_AUTH_LOGOUT_HEADER } from "../../app.service";
import { Router } from "@angular/router";
import { CryptoService } from "./crypto.service";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { LoginService } from "../login/login.service";
import { ForgotPasswordService } from "../components/forgot/forgot-password/forgot-password.service";

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  ENCRYPT_API = environment.encrypt_api;
  plataformaId = environment.platform_id;
  mobileFlag = false;

  constructor(
    private appService: AppService,
    private loginService: LoginService,
    private ForgotPasswordService: ForgotPasswordService,
    private router: Router,
    private cryptoService: CryptoService
  ) {
    // Subscreve para obter o estado do aplicativo móvel
    this.appService.getMobileApp().subscribe((flag) => {
      this.mobileFlag = flag;
    });
  }

  /**
   * Método intercepta requisições HTTP para adicionar cabeçalhos e, se necessário, criptografar o corpo.
   * @param request - Requisição HTTP original.
   * @param next - Próximo manipulador no pipeline.
   * @returns Observable com o evento HTTP modificado.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const hasSkipAuthLogoutHeader = request.headers.has(SKIP_AUTH_LOGOUT_HEADER);
    const skipAuthLogout = hasSkipAuthLogoutHeader && request.url.includes('/api/module/user/id');
    const interceptedRequest = hasSkipAuthLogoutHeader
      ? request.clone({ headers: request.headers.delete(SKIP_AUTH_LOGOUT_HEADER) })
      : request;
    let cloneReq;

    if (this.isExemptedRequest(interceptedRequest)) {
      this.ENCRYPT_API = false;
      cloneReq = this.cloneRequestForExemptedUrls(interceptedRequest);
    } else {
      this.ENCRYPT_API = environment.encrypt_api;
      cloneReq = this.processRequest(interceptedRequest);
    }

    cloneReq = this.addAccessKeyToRequest(cloneReq);

    if (environment.disable_cache) {
      cloneReq = cloneReq.clone({
        setHeaders: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
    }

    if (this.isEncryptionRequired(cloneReq)) {
      cloneReq = this.encryptRequestBody(cloneReq);
    }

    const authToken = cloneReq.headers.get("authorization");

    return next.handle(cloneReq).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          let responseBody = event.body;
          if (this.ENCRYPT_API && responseBody) {
            responseBody = this.cryptoService.decryptResponse(responseBody, authToken);
            event = event.clone({ body: responseBody });
          }

          if (!skipAuthLogout && responseBody && (responseBody.code == environment.logout.code || responseBody.error == environment.logout.error || responseBody.msg == environment.logout.msg)) {
            this.handleLogout();
          }
        }
        return event;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        try {
          let errorBody = errorResponse.error;
          if (this.ENCRYPT_API && errorBody && typeof errorBody === "object" && errorBody.data) {
            try {
              errorBody = this.cryptoService.decryptResponse(errorBody, authToken);
            } catch (decryptError) {
              console.error("Falha ao descriptografar erro:", decryptError);
            }
          } else {
          }

          const checkLogoutCondition = (obj: any): boolean => {
            if (!obj) return false;

            if (obj.code == environment.logout.code || obj.error == environment.logout.error || obj.msg == environment.logout.msg) {
              return true;
            }

            if (obj.error && typeof obj.error === "object") {
              if (obj.error.code == environment.logout.code || obj.error.error == environment.logout.error || obj.error.msg == environment.logout.msg) {
                return true;
              }
            }

            return false;
          };

          if (!skipAuthLogout && (errorResponse.status == environment.logout.code || checkLogoutCondition(errorBody))) {
            this.handleLogout();
          }
        } catch (e) {}

        return throwError(errorResponse);
      }),

      finalize(() => {})
    );
  }

  /**
   * Verifica se a requisição deve ser isenta de criptografia.
   * @param request - Requisição HTTP.
   * @returns true se a requisição é isenta, false caso contrário.
   */
  private isExemptedRequest(request: HttpRequest<any>): boolean {
    const exemptedUrls = ["geolocation/usuario/lgpd/cookies", "module/user/login/diagrama/token", "software/user/login/token", "software/download", "viacep.com.br", "software/versions", "api/newtecnoscopio/foto/download", "api/newtecnoscopio/pfd/download", "googleapis.com/youtube/v3"];

    const urlExempted = exemptedUrls.some((exemptedUrl) => request.url.includes(exemptedUrl));
    const hrefExempted = window.location.href.includes("diagrama/crf");

    return urlExempted || hrefExempted;
  }

  /**
   * Clona a requisição para URLs isentas.
   * @param request - Requisição HTTP original.
   * @returns Requisição clonada com cabeçalhos apropriados.
   */
  private cloneRequestForExemptedUrls(request: HttpRequest<any>): HttpRequest<any> {
    if (request.url.includes("googleapis.com/youtube/v3")) {
      return request.clone();
    } else {
      return request.clone({
        setHeaders: {
          cryt: "d17",
          plataformaId: this.plataformaId,
        },
      });
    }
  }

  /**
   * Processa a requisição para adicionar cabeçalhos e determinar a criptografia necessária.
   * @param request - Requisição HTTP original.
   * @returns Requisição clonada com cabeçalhos apropriados.
   */
  private processRequest(request: HttpRequest<any>): HttpRequest<any> {
    if (this.ENCRYPT_API) {
      this.appService.user.subscribe((user) => {
        if (user && user.crudPermission.admin) {
          this.ENCRYPT_API = false;
        }
      });
    }

    return request.clone({
      setHeaders: {
        cryt: this.ENCRYPT_API ? "r10" : "d17",
        plataformaId: this.plataformaId,
      },
    });
  }

  /**
   * Adiciona a chave de acesso do localStorage à requisição.
   * @param request - Requisição HTTP original ou clonada.
   * @returns Requisição clonada com a chave de acesso.
   */
  private addAccessKeyToRequest(request: HttpRequest<any>): HttpRequest<any> {
    const accessKey = localStorage.getItem("acesskey");
    if (accessKey) {
      return request.clone({
        setHeaders: {
          acesskey: accessKey,
          plataformaId: this.plataformaId,
          cryt: this.ENCRYPT_API ? "r10" : "d17",
        },
      });
    }
    return request;
  }

  /**
   * Verifica se a criptografia é necessária para a requisição.
   * @param request - Requisição HTTP clonada.
   * @returns true se a criptografia é necessária, false caso contrário.
   */
  private isEncryptionRequired(request: HttpRequest<any>): boolean {
    return request instanceof HttpRequest && (request.method === "POST" || request.method === "PUT") && this.ENCRYPT_API;
  }

  /**
   * Criptografa o corpo da requisição.
   * @param request - Requisição HTTP clonada.
   * @returns Requisição clonada com o corpo criptografado.
   */
  private encryptRequestBody(request: HttpRequest<any>): HttpRequest<any> {
    const body = this.cryptoService.cripyBody(request.body, request.headers.get("authorization"));
    return request.clone({ body });
  }

  /**
   * Trata o logout do usuário.
   */
  private handleLogout(): void {
    if (this.mobileFlag) {
      const message = { type: "logout-angular-website", payload: true };
      window.parent.postMessage(message, "*");
    }
    this.appService.logoutUser();
  }

  /**
   * Método para realizar login falso, utilizado para desenvolvimento e testes.
   */
  fakeLogin() {
    try {
      this.loginService.authentication({ email: environment.user_fake.email, password: environment.user_fake.password }).subscribe(
        (res: any) => {
          if (res.code == 200) {
            localStorage.clear();
            sessionStorage.clear();
            this.ForgotPasswordService.storageEmail(res.user.auth.email);
            this.appService.emit(res.user, res.token, null, "from login component");
            this.appService.setFakeLogin(true);
            localStorage.setItem("fake-user", "true");
          }
        },
        (error) => {
          // Falha no login falso
        }
      );
    } catch (error) {
      // Erro no login falso
    }
  }
}
